import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Field, Button, inr } from "../components/UI";
import Typeahead from "../components/Typeahead";
import { FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const searchCustomersAsync = async (q) => {
  const url = q ? `/customers?q=${encodeURIComponent(q)}&limit=50` : "/customers?limit=50";
  const r = await api.get(url);
  return r.data;
};

export default function Quotations() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");
  const [form, setForm] = useState({ customer_id: "", items: [], notes: "", valid_until: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [q, p] = await Promise.all([api.get("/quotations"), api.get("/products")]);
      setList(q.data); setProducts(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", product_name: "", quantity: 1, unit_price: 0 }] });
  const updateItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "product_id" && v) {
      const p = products.find((x) => x.id === v);
      if (p) { items[i].product_name = p.name; items[i].unit_price = p.default_price; }
    }
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * 0.18;

  const openNew = () => {
    setForm({ customer_id: "", items: [{ product_id: "", product_name: "", quantity: 1, unit_price: 0 }], notes: "", valid_until: "" });
    setSelectedCustomerLabel("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id || form.items.length === 0) { toast.error("Customer and items required"); return; }
    try {
      const payload = { ...form, items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })) };
      await api.post("/quotations", payload);
      toast.success("Quotation created"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete ${q.quote_no}?`)) return;
    try { await api.delete(`/quotations/${q.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl">
      <PageHeader
        title="Quotations"
        subtitle="Generate estimates for prospective customers"
        action={<Button onClick={openNew} data-testid="add-quote-btn"><Plus size={16}/>New Quotation</Button>}
      />
      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : list.length === 0 ? (
        <EmptyState icon={FileText} title="No quotations yet" hint="Send professional price estimates to win new business."
          action={<Button onClick={openNew}><Plus size={16}/> New Quotation</Button>} />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="crm-table" data-testid="quotes-table">
            <thead>
              <tr>
                <th>Quote #</th><th>Customer</th><th>Items</th>
                <th className="text-right">Subtotal</th><th className="text-right">Tax</th><th className="text-right">Total</th>
                <th>Valid Until</th><th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                  <td className="font-mono-num font-semibold text-[#C1121F]">{q.quote_no}</td>
                  <td>{q.customer_name}</td>
                  <td className="text-xs text-[#475569]">{q.items.length} item(s)</td>
                  <td className="text-right font-mono-num">{inr(q.subtotal)}</td>
                  <td className="text-right font-mono-num text-[#475569]">{inr(q.tax)}</td>
                  <td className="text-right font-mono-num font-semibold">{inr(q.total)}</td>
                  <td className="text-xs">{q.valid_until || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => remove(q)} data-testid={`delete-quote-${q.id}`} className="p-1.5 text-[#475569] hover:text-red-600"><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Quotation" maxWidth="max-w-3xl">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer" required>
              <Typeahead
                loadOptions={searchCustomersAsync}
                value={form.customer_id}
                selectedLabel={selectedCustomerLabel}
                onChange={(id, opt) => { setForm({...form, customer_id: id}); setSelectedCustomerLabel(opt?.name || ""); }}
                placeholder="Type to search customer…"
                required
                testid="quote-customer"
                renderMeta={(c) => `${c.type || ""} ${c.phone ? "· " + c.phone : ""} ${c.city ? "· " + c.city : ""}`}
              />
            </Field>
            <Field label="Valid Until">
              <input type="date" value={form.valid_until} onChange={(e) => setForm({...form, valid_until: e.target.value})} className="input-base" />
            </Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">Items</label>
              <Button type="button" variant="secondary" onClick={addItem}><Plus size={14}/> Add item</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-4">
                    <Typeahead
                      options={products}
                      value={it.product_id || ""}
                      onChange={(id) => updateItem(i, "product_id", id)}
                      placeholder="Search product…"
                      testid={`quote-product-${i}`}
                    />
                  </div>
                  <input placeholder="Name" value={it.product_name} onChange={(e) => updateItem(i, "product_name", e.target.value)} required className="input-base col-span-3" />
                  <input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="input-base col-span-2" />
                  <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="input-base col-span-2" />
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-red-500 p-2"><Trash2 size={16}/></button>
                </div>
              ))}
            </div>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} />
          </Field>
          <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
            <div className="text-sm text-[#475569] space-y-0.5">
              <div>Subtotal: <span className="font-mono-num font-semibold">{inr(subtotal)}</span></div>
              <div>Tax (18% GST): <span className="font-mono-num font-semibold">{inr(tax)}</span></div>
              <div className="text-[#C1121F] font-bold text-lg font-mono-num">Total: {inr(subtotal + tax)}</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-quote-btn">Create</Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
